const SUPPORT_EMAIL = 'concierge@mento.co';

export const withSupportNudge = (message: string): string =>
  `${message}\n\nIf this keeps happening, email ${SUPPORT_EMAIL}.`;

type MfaErrorInfo = {
  type: string | null;
  message: string | null;
};

export const getMfaErrorInfo = (error: unknown): MfaErrorInfo => {
  if (!error || typeof error !== 'object') return { type: null, message: null };

  const err = error as {
    error_type?: string;
    errorType?: string;
    error_message?: string;
    errorMessage?: string;
    data?: unknown;
  };

  const topLevelType = err.errorType || err.error_type;
  const topLevelMessage = err.errorMessage || err.error_message;
  if (topLevelType)
    return { type: topLevelType, message: topLevelMessage || null };

  if (typeof err.data === 'string') {
    return { type: err.data, message: err.data };
  }

  if (typeof err.data === 'object' && err.data) {
    const data = err.data as {
      error?: string;
      error_type?: string;
      errorType?: string;
      error_message?: string;
      errorMessage?: string;
      message?: string;
    };

    const nestedType = data.errorType || data.error_type;
    const nestedMessage = data.errorMessage || data.error_message;
    if (nestedType) return { type: nestedType, message: nestedMessage || null };

    // Backend errors often use { error: 'invalid_request' } (not a Stytch error payload).
    if (
      data.error &&
      data.error !== 'stytch_error' &&
      data.error !== 'rate_limited'
    )
      return { type: data.error, message: data.message || null };
  }

  return { type: null, message: null };
};

export const getMfaErrorType = (error: unknown): string | null =>
  getMfaErrorInfo(error).type;

const getLowerCaseErrorStrings = (error: unknown): string[] => {
  if (!error || typeof error !== 'object') return [];

  const err = error as {
    error_message?: string;
    errorMessage?: string;
    message?: string;
    data?: unknown;
  };

  const values = [err.error_message, err.errorMessage, err.message];

  if (typeof err.data === 'object' && err.data) {
    const data = err.data as {
      error?: string;
      error_message?: string;
      errorMessage?: string;
      message?: string;
      details?: string[];
    };

    values.push(
      data.error,
      data.error_message,
      data.errorMessage,
      data.message,
    );
    if (Array.isArray(data.details)) values.push(...data.details);
  }

  return values
    .filter(
      (value): value is string => typeof value === 'string' && value.length > 0,
    )
    .map((value) => value.toLowerCase());
};

export const shouldTriggerTotpReenrollment = (error: unknown): boolean => {
  const errorType = getMfaErrorType(error);
  if (
    errorType === 'totp_not_found' ||
    errorType === 'totp_enrollment_required'
  ) {
    return true;
  }

  return getLowerCaseErrorStrings(error).some(
    (value) =>
      value.includes('totp is invalid') ||
      value.includes('totp invalid') ||
      value.includes('authenticator app needs to be set up again'),
  );
};

export const shouldSkipSupportNudge = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const err = error as { status?: number; data?: unknown };
  if (err.status === 429) return true;
  if (typeof err.data === 'object' && err.data) {
    const data = err.data as { error?: string };
    if (data.error === 'rate_limited') return true;
  }
  return false;
};

const errorTypeToFriendlyMessage = (
  errorType: string,
  fallbackMessage?: string,
): string | null => {
  switch (errorType) {
    case 'email_verification_required':
    case 'mfa_verification_required':
      return "Verify your email to continue setting up MFA. We'll email you a verification link.";
    case 'too_many_unverified_factors':
      // Stytch uses this error type when the user has an unverified email factor and attempts to add another factor.
      return "Verify your email to continue setting up MFA. We'll email you a verification link.";
    case 'totp_not_found':
    case 'totp_enrollment_required':
      return 'Your authenticator app needs to be set up again. Starting setup now...';
    default:
      return fallbackMessage ?? null;
  }
};

export const stytchErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object')
    return 'Something went wrong. Please try again.';

  const err = error as {
    error_message?: string;
    error_type?: string;
    errorMessage?: string;
    errorType?: string;
    message?: string;
    data?: unknown;
    status?: number;
  };

  // RTK Query (fetchBaseQuery) commonly returns { status: 429, data: { error: "rate_limited" } }
  // Prefer a user-friendly message over exposing internal error codes.
  if (err.status === 429)
    return 'Too many attempts. Please wait 1 minute and try again.';

  const errorInfo = getMfaErrorInfo(error);
  if (errorInfo.type) {
    const friendly = errorTypeToFriendlyMessage(
      errorInfo.type,
      errorInfo.message || undefined,
    );
    if (friendly) return friendly;
  }

  if (typeof err.data === 'object' && err.data) {
    const data = err.data as {
      error?: string;
      message?: string;
      details?: string[];
    };

    if (data.error === 'rate_limited')
      return 'Too many attempts. Please wait 1 minute and try again.';

    if (data.error && data.message) return `${data.error}: ${data.message}`;
    if (data.error && data.details?.length)
      return `${data.error}: ${data.details.join(', ')}`;
    if (data.error) return data.error;
  }

  if (typeof err.data === 'string' && err.data.length > 0) {
    return err.data;
  }

  return (
    err.errorMessage ||
    err.error_message ||
    err.message ||
    'Something went wrong. Please try again.'
  );
};

export const formatMfaErrorMessage = (error: unknown): string => {
  const msg = stytchErrorMessage(error);
  return shouldSkipSupportNudge(error) ? msg : withSupportNudge(msg);
};
